
import {
  computed,
  defineComponent, inject, onMounted, ref, Ref, watch,
} from 'vue';
import {
  Form, defineRule, useForm, configure,
} from 'vee-validate';
import { useRoute, useRouter } from 'vue-router';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
import AppInput from '@/components/UI/AppInput.vue';
import AppButton from '@/components/UI/AppButton.vue';
import AppAlert from '@/components/UI/AppAlert.vue';
import ValidationService from '@/services/ValidationService';
import { ICredentials } from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import AuthService from '@/modules/auth/services/AuthService';
// import LoginHeader from '@/modules/auth/components/LoginHeader.vue';
import AppLoginRegister from '@/components/AppLoginRegister.vue';
import AppHeader from '@/components/AppHeader.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
import { PERFORMANCE_MONITORING_STEPS, REGISTRATION_STATE } from '@/types/enums';

defineRule('required', (value: string) => ValidationService.isNotEmpty(value) || 'error.required');
defineRule('email', (value: string) => ValidationService.isEmail(value) || 'error.email');

configure({
  validateOnBlur: false, // controls if `blur` events should trigger validation with `handleChange` handler
  validateOnChange: false, // controls if `change` events should trigger validation with `handleChange` handler
  validateOnInput: false, // controls if `input` events should trigger validation with `handleChange` handler
  validateOnModelUpdate: false, // controls if `update:modelValue` events should trigger validation with `handleChange` handler
});

export default defineComponent({
  components: {
    AppLoginRegister,
    Form,
    AppInput,
    AppButton,
    AppAlert,
    AppHeader,
    AppLoader,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const errorKey = ref('');
    const inputEmail: Ref<any | null> = ref(null);
    const emailError = ref('');
    const passwordRequired = ref('');
    const pass: Ref<any | null> = ref(null);
    const schema = {
      email: 'required',
      // password: 'required',
    };
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;
    const title = computed(() => $localize(route.meta?.title));

    const mainStore = useMainStore();
    const dataRequest: Ref<boolean> = ref(false);

    const removeEventSpaces = (e) => {
      e.preventDefault();
      const left = e.target.value.substring(0, e.target.selectionStart);
      const right = e.target.value.substring(e.target.selectionEnd, e.target.value.length);
      const pasted = (e.dataTransfer || e.clipboardData).getData('text').replace(/ /g, '');
      inputEmail.value.value = left + pasted + right;
    };

    function focusNextInput(nextInputId: string) {
      document.getElementById(nextInputId)!.focus();
    }

    function onSubmit(values: ICredentials) {
      dataRequest.value = true;
      AuthService.login(values).then((response) => {
        if (response.success) {
          if (response.results.user.registrationState === REGISTRATION_STATE.NOT_REGISTERED) {
            router.push('/registration/healthcare').then(() => {
              dataRequest.value = false;
            });
          } else if (response.results.user.registrationState === REGISTRATION_STATE.CONFIRM_PHONE_NUMBER) {
            router.push('/registration/step-7').then(() => {
              dataRequest.value = false;
            });
          } else if (response.results.user.registrationState !== REGISTRATION_STATE.CONFIRM_PHONE_NUMBER && !mainStore.isMobile) {
            router.push('/drive').then(() => {
              dataRequest.value = false;
            });
          } else {
            router.push('/').then(() => {
              dataRequest.value = false;
            });
          }
        }
        if (!response.success) {
          dataRequest.value = false;
          errorKey.value = response.errorKey;
        }
      });
    }

    function startSubmit(values: ICredentials) {
      const inp = document.getElementById('passInput') as HTMLInputElement;
      // eslint-disable-next-line no-param-reassign
      values.password = inp.value;

      const email = values.email.trim();
      // eslint-disable-next-line no-param-reassign
      values.email = email;

      if (!ValidationService.isEmail(values.email)) {
        emailError.value = $localize('error.email');
        return;
      }
      if (!ValidationService.isNotEmpty(values.password)) {
        passwordRequired.value = $localize('error.required');
        return;
      }
      onSubmit(values);
    }

    const emailInputValue = computed(() => inputEmail.value?.value);
    watch(() => emailInputValue.value, (nv, ov) => {
      if ((nv !== ov) && (ov !== null)) {
        emailError.value = '';
      }
    });
    const passInputValue = computed(() => pass.value?.value);
    watch(() => passInputValue.value, (nv, ov) => {
      if ((nv !== ov) && (ov !== null)) {
        passwordRequired.value = '';
      }
    });

    function sendPerformanceAnalitics() {
      if (MonitoringService.isFirstPageMonitored) {
        MonitoringService.sendPageMountedSpeed();
      } else {
        MonitoringService.isFirstPageMonitored = true;
        MonitoringService.sendMonitoringStep(PERFORMANCE_MONITORING_STEPS.FIRST_PAGE_MOUNTED_LOGIN, performance.now());
      }
    }

    onMounted(() => {
      sendPerformanceAnalitics();
    });

    return {
      errorKey,
      schema,
      mainStore,
      title,
      focusNextInput,
      removeEventSpaces,
      onSubmit,
      inputEmail,
      dataRequest,
      pass,
      startSubmit,
      emailError,
      emailInputValue,
      passwordRequired,
    };
  },
});
